/* eslint-disable @atlassian/relay/graphql-naming */
import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { graphql, useFragment } from 'react-relay';
import { evaluateRule } from '@atlassian/jira-issue-table-conditional-formatting/src/services/evaluate-rule/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { FieldValue } from '@atlassian/jira-issue-table-conditional-formatting/src/services/evaluate-rule/types.tsx';
import { formattingColors } from '@atlassian/jira-issue-table-conditional-formatting/src/common/utils/formatting-colors/index.tsx';
import { useIsConditionalFormattingEnabled } from '../../../controllers/features/selectors.tsx';
import type { useRowColor_savedView_rules$key } from './__generated__/useRowColor_savedView_rules.graphql';
import type { useRowColor_rules$key } from './__generated__/useRowColor_rules.graphql';
import type { useRowColor$key } from './__generated__/useRowColor.graphql';

export const useRowColor = (
	fieldSets: useRowColor$key | undefined | null,
	project: useRowColor_rules$key | undefined | null,
	formattingRules?: useRowColor_savedView_rules$key | null | undefined,
) => {
	const isConditionalFormattingEnabled =
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fg('jira_list_conditional_formatting') && useIsConditionalFormattingEnabled();

	const isSavedViewConditionalFormattingEnabled =
		fg('saved_list_view_technical_changes') && isConditionalFormattingEnabled;

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const fields = useFragment<useRowColor$key>(
		graphql`
			fragment useRowColor on JiraIssueFieldSetConnection {
				edges {
					node {
						fields {
							edges {
								node {
									__typename
									fieldId
									... on JiraSingleLineTextField {
										text
									}
									... on JiraComponentsField {
										selectedComponentsConnection(first: 1000) {
											edges {
												node {
													componentId
												}
											}
										}
									}
									... on JiraLabelsField {
										selectedLabelsConnection(first: 1000) {
											edges {
												node {
													name
												}
											}
										}
									}
									... on JiraSingleSelectUserPickerField {
										user {
											accountId
											id
										}
									}
									... on JiraMultipleSelectUserPickerField {
										selectedUsersConnection {
											edges {
												node {
													accountId
												}
											}
										}
									}
									... on JiraDatePickerField {
										date
									}
									... on JiraDateTimePickerField {
										dateTime
									}
									... on JiraNumberField {
										number
									}
									... on JiraPriorityField {
										priority {
											priorityId
										}
									}
									... on JiraProjectField {
										project {
											projectId
										}
									}
									... on JiraSprintField {
										selectedSprintsConnection {
											edges {
												node {
													sprintId
												}
											}
										}
									}
									... on JiraSingleVersionPickerField {
										version {
											versionId
										}
									}
									... on JiraIssueTypeField {
										issueType {
											issueTypeId
										}
									}
									... on JiraStatusField {
										status {
											statusId
										}
									}
									... on JiraMultipleVersionPickerField {
										selectedVersionsConnection(first: 1000) {
											edges {
												node {
													versionId
												}
											}
										}
									}
									... on JiraSingleSelectField {
										selectedValue {
											id
										}
									}
									... on JiraMultipleSelectField {
										selectedValues {
											edges {
												node {
													id
												}
											}
										}
									}
									... on JiraRadioSelectField {
										selectedOption {
											label: value
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		isConditionalFormattingEnabled ? fieldSets : null,
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const rulesOld = useFragment<useRowColor_rules$key>(
		graphql`
			fragment useRowColor_rules on JiraProject {
				conditionalFormattingRules(first: 20) {
					edges {
						node {
							expression {
								... on JiraFormattingSingleValueOperand {
									fieldId
									singleValueOperator: operator
									value
								}
								... on JiraFormattingMultipleValueOperand {
									fieldId
									multipleValueOperator: operator
									values
								}
								... on JiraFormattingNoValueOperand {
									fieldId
									noValueOperator: operator
								}
							}
							formattingArea
							formattingColor {
								id
								colorKey
							}
							id
						}
					}
				}
			}
		`,
		isConditionalFormattingEnabled ? (project ?? null) : null,
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const rulesNew = useFragment<useRowColor_savedView_rules$key>(
		graphql`
			fragment useRowColor_savedView_rules on JiraFormattingRuleConnection {
				edges {
					node {
						expression {
							... on JiraFormattingSingleValueOperand {
								fieldId
								singleValueOperator: operator
								value
							}
							... on JiraFormattingMultipleValueOperand {
								fieldId
								multipleValueOperator: operator
								values
							}
							... on JiraFormattingNoValueOperand {
								fieldId
								noValueOperator: operator
							}
						}
						formattingArea
						formattingColor {
							id
							colorKey
						}
						id
					}
				}
			}
		`,
		isSavedViewConditionalFormattingEnabled ? formattingRules : null,
	);

	const rules = isSavedViewConditionalFormattingEnabled
		? rulesNew
		: rulesOld?.conditionalFormattingRules;

	const valuesByFieldId: Map<string, FieldValue> | null = useMemo(() => {
		if (
			!isConditionalFormattingEnabled ||
			rules?.edges == null ||
			rules?.edges.length === 0 ||
			fields?.edges == null ||
			fields?.edges?.length === 0
		) {
			return null;
		}

		const localMap = new Map<string, FieldValue>();
		fields.edges.forEach((fieldSet) =>
			fieldSet?.node?.fields?.edges?.forEach((fieldEdge) => {
				const field = fieldEdge?.node;
				switch (field?.__typename) {
					case 'JiraSingleLineTextField':
						localMap.set(field.fieldId, field.text);
						break;
					case 'JiraLabelsField': {
						const names =
							field.selectedLabelsConnection?.edges
								?.map((edge) => edge?.node?.name)
								.filter((name: string | undefined | null): name is string => name != null) ?? [];
						localMap.set(field.fieldId, names);
						break;
					}
					case 'JiraComponentsField': {
						const ids =
							field.selectedComponentsConnection?.edges
								?.map((edge) => edge?.node?.componentId)
								.filter(
									(componentId: string | undefined): componentId is string => componentId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraSingleSelectUserPickerField': {
						localMap.set(field.fieldId, field.user?.accountId ?? 'UNASSIGNED');
						break;
					}
					case 'JiraDatePickerField': {
						const parsedDate = field.date ? parseISO(field.date) : undefined;
						localMap.set(field.fieldId, parsedDate);
						break;
					}
					case 'JiraDateTimePickerField': {
						const parsedDate = field.dateTime ? parseISO(field.dateTime) : undefined;
						localMap.set(field.fieldId, parsedDate);
						break;
					}
					case 'JiraNumberField': {
						localMap.set(field.fieldId, field.number);
						break;
					}
					case 'JiraPriorityField': {
						localMap.set(field.fieldId, field.priority?.priorityId);
						break;
					}
					case 'JiraProjectField': {
						localMap.set(field.fieldId, field.project?.projectId);
						break;
					}
					case 'JiraSingleVersionPickerField': {
						localMap.set(field.fieldId, field.version?.versionId);
						break;
					}
					case 'JiraIssueTypeField': {
						localMap.set(field.fieldId, field.issueType?.issueTypeId);
						break;
					}
					case 'JiraStatusField': {
						localMap.set(field.fieldId, field.status?.statusId);
						break;
					}
					case 'JiraMultipleSelectUserPickerField': {
						const ids =
							field.selectedUsersConnection?.edges
								?.map((edge) => edge?.node?.accountId)
								.filter(
									(componentId: string | undefined): componentId is string => componentId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraSprintField': {
						const ids =
							field.selectedSprintsConnection?.edges
								?.map((edge) => edge?.node?.sprintId)
								.filter((sprintId: string | undefined): sprintId is string => sprintId != null) ??
							[];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraMultipleVersionPickerField': {
						const ids =
							field.selectedVersionsConnection?.edges
								?.map((edge) => edge?.node?.versionId)
								.filter(
									(versionId: string | undefined): versionId is string => versionId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraSingleSelectField': {
						localMap.set(field.fieldId, field.selectedValue?.id);
						break;
					}
					case 'JiraMultipleSelectField': {
						const ids =
							field.selectedValues?.edges
								?.map((edge) => edge?.node?.id)
								.filter(
									(selectedValueId: string | undefined): selectedValueId is string =>
										selectedValueId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraRadioSelectField': {
						localMap.set(field.fieldId, field.selectedOption?.label);
						break;
					}

					// TODO: Add all supported types value mapping
					default:
						break;
				}
			}),
		);
		return localMap;
	}, [fields, isConditionalFormattingEnabled, rules]);

	const formatRule = useMemo(() => {
		const formattingRulesEdges = rules?.edges;
		if (
			formattingRulesEdges == null ||
			formattingRulesEdges.length === 0 ||
			!isConditionalFormattingEnabled ||
			valuesByFieldId == null
		)
			return null;

		for (const rule of formattingRulesEdges) {
			const isRowRule = rule?.node?.formattingArea === 'ROW';
			const expression = rule?.node?.expression;
			const fieldId = expression?.fieldId;
			const value = expression?.value ?? expression?.values;
			const operator =
				expression?.noValueOperator ??
				expression?.singleValueOperator ??
				expression?.multipleValueOperator;

			if (fieldId && operator && isRowRule) {
				const fieldValue = valuesByFieldId.get(fieldId);
				const ruleResult = evaluateRule(operator, fieldValue, value);
				if (ruleResult) {
					return rule;
				}
			}
		}
		return null;
	}, [valuesByFieldId, rules, isConditionalFormattingEnabled]);

	const key = formatRule?.node?.formattingColor?.colorKey;
	if (!key) return null;

	return formattingColors[key];
};
